import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Plus, Trash, PencilSimple, X, FloppyDisk, Sparkle, CircleNotch, ClockClockwise } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const CATS = ["Payments & Banking", "Sourcing & Trust", "Sourcing", "Logistics", "Private Label", "Markets", "Quality"];
const EMPTY = { title: "", excerpt: "", content: "", category: "Sourcing & Trust", cover_image: "", published: true };
const date = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function AiGeneratorCard({ onPublished }) {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Sourcing & Trust");
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { topic, category }, { timeout: 120000 });
      toast.success(`Published live: ${data.title}`);
      setTopic("");
      onPublished();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed — please retry");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-xl border border-teal/40 bg-gradient-to-r from-[#0A192F] to-[#12365c] p-6 text-white" data-testid="ai-blog-generator">
      <div className="flex items-center gap-2">
        <Sparkle size={20} weight="fill" className="text-teal-light" />
        <h3 className="font-heading text-lg font-semibold">Create with AI</h3>
      </div>
      <p className="mt-1 text-xs text-slate-300">Enter a topic — AI drafts the full article, headline and excerpt, then publishes it live instantly (FR & SW translations are generated automatically).</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          placeholder="Topic (e.g. How to pay Chinese suppliers)"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && generate()}
          disabled={generating}
          data-testid="ai-blog-topic"
          className="flex-1 rounded-md border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-teal"
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={generating} data-testid="ai-blog-category"
          className="rounded-md border border-white/20 bg-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-teal [&>option]:text-ink">
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button onClick={generate} disabled={generating} data-testid="ai-blog-generate"
          className="flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-6 py-2.5 text-sm font-semibold disabled:opacity-60">
          {generating ? <><CircleNotch size={16} className="animate-spin" /> Generating…</> : <><Sparkle size={16} weight="fill" /> Generate with AI</>}
        </button>
      </div>
      {generating && <p className="mt-3 text-xs text-teal-light" data-testid="ai-blog-progress">Writing the article… this takes ~30 seconds. It will publish automatically.</p>}
    </div>
  );
}

function AutoScheduleCard() {
  const [schedule, setSchedule] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blog/schedule").then((r) => setSchedule(r.data)).catch((e) => console.error("Failed to load schedule:", e));
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!schedule || saving) return;
    setSaving(true);
    const enabling = !schedule.enabled;
    try {
      const { data } = await api.put("/admin/blog/schedule", { enabled: enabling }, { timeout: 120000 });
      setSchedule(data);
      if (data.published_now) toast.success(`Auto-publish is ON — first article published: ${data.last_title}`);
      else toast.success(enabling ? "Weekly auto-publish enabled" : "Auto-publish paused");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update schedule");
    } finally { setSaving(false); }
  };

  const fmtNext = (v) => {
    if (!v) return "—";
    if (v === "pending") return "shortly";
    return new Date(v).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="blog-auto-schedule">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white">
          <ClockClockwise size={22} weight="duotone" />
        </div>
        <div>
          <h3 className="font-heading text-sm font-semibold text-ink">Weekly auto-publish</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            AI writes and publishes a fresh sourcing article every {schedule?.interval_days || 7} days (with FR & SW translations).
          </p>
          {schedule?.enabled && (
            <p className="mt-1 text-xs text-teal" data-testid="schedule-status">
              Next article: {fmtNext(schedule.next_run)}
              {schedule.last_title && <span className="text-slate-400"> · Last: "{schedule.last_title}"</span>}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={!schedule || saving}
        data-testid="schedule-toggle"
        role="switch"
        aria-checked={!!schedule?.enabled}
        className={`relative h-7 w-14 shrink-0 rounded-full transition-colors duration-300 disabled:opacity-50 ${schedule?.enabled ? "bg-teal" : "bg-slate-300"}`}
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${schedule?.enabled ? "left-8" : "left-1"}`} />
      </button>
    </div>
  );
}

export function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/blog", { params: { lang: "en", limit: 100 } }).then((r) => setPosts(r.data)).catch((e) => console.error("Failed to load posts:", e));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await api.put(`/admin/blog/${editId}`, form); toast.success("Article updated"); }
      else { await api.post("/admin/blog", form); toast.success("Article published"); }
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const edit = async (p) => {
    try {
      const { data } = await api.get(`/blog/${p.slug}`, { params: { lang: "en" } });
      setEditId(p.id);
      setForm({ title: data.title, excerpt: data.excerpt, content: data.content || "", category: data.category, cover_image: data.cover_image || "", published: data.published !== false });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not load post"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-manager">
      <AiGeneratorCard onPublished={load} />
      <AutoScheduleCard />

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-blog-form">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-ink">{editId ? "Edit article" : "Write manually"}</h3>
            {editId && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-ink"><X size={14} /> Cancel</button>}
          </div>
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" className={field} />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="blog-cover" className={field} />
          <ImageUpload value={form.cover_image} onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))} label="Upload cover image" testid="blog-cover-upload" />
          <textarea required rows={2} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt" className={field} />
          <textarea required rows={6} placeholder="Content (use ## for headings)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content" className={field} />
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-published" /> Published</label>
          <p className="text-[11px] text-slate-400">French & Swahili translations are generated automatically after saving.</p>
          <button type="submit" data-testid="blog-submit" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800">
            {editId ? <><FloppyDisk size={16} weight="bold" /> Save changes</> : <><Plus size={16} weight="bold" /> Publish</>}
          </button>
        </form>
        <div className="space-y-3">
          <h3 className="font-heading text-lg font-semibold text-ink">Articles ({posts.length})</h3>
          {posts.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-post-${p.id}`}>
              <div>
                <p className="font-heading text-sm font-semibold text-ink">{p.title}</p>
                <p className="text-xs text-slate-400">
                  {p.category} · {date(p.created_at)}
                  {p.ai_generated && <span className="ml-2 rounded bg-teal/10 px-1.5 py-0.5 text-[10px] font-semibold text-teal">AI</span>}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => edit(p)} data-testid={`edit-post-${p.id}`} className="text-slate-400 hover:text-teal"><PencilSimple size={18} /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-post-${p.id}`} className="text-slate-400 hover:text-red-600"><Trash size={18} /></button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No articles yet.</p>}
        </div>
      </div>
    </div>
  );
}
